import { useContext, useState, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { BusinessIdentityProvider, IdentityProvider, CountryIdUtil, CurrencyUtil } from '@izettle/constants';
import { UserContext } from '@izettle/react';
import { useService } from '../hooks/use-service.js';
import { convertKeysFromSnakeToCamelCase, transformSettings } from '../utils/rest.js';
import { OverridesContext } from './overrides.js';
import { KnownService } from './services.js';
import { jsx } from 'react/jsx-runtime';
import { useAuthClient } from '../hooks/use-auth.js';

const fetchUser = async (sunUrl, authClient, setUser, onReceiveUser) => {
  const token = await authClient.getToken();
  const promises = [fetch(`${sunUrl}/resources/user`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), fetch(`${sunUrl}/resources/user-profiles/self`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), fetch(`${sunUrl}/resources/features/all`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), fetch(`${sunUrl}/resources/user/settings`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), fetch(`${sunUrl}/resources/organization/settings`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })];
  const [userResponse, profileResponse, featuresResponse, userSettingsResponse, organizationSettingsResponse] = await Promise.all(promises);
  const userData = await userResponse.json();
  const camelCasedUserData = convertKeysFromSnakeToCamelCase(userData);
  const {
    data: {
      identityProvider,
      identityProviderId,
      businessIdentityProvider,
      userUUID
    }
  } = await profileResponse.json();
  camelCasedUserData.user.fullUserUuid = userUUID;
  const featuresData = await featuresResponse.json();
  const userSettingsJson = await userSettingsResponse.json();
  camelCasedUserData.user.settings = transformSettings(userSettingsJson);
  const organizationSettingsJson = await organizationSettingsResponse.json();
  camelCasedUserData.organization.settings = transformSettings(organizationSettingsJson);
  const isMobileApp = ["iZettle-Android", "iZettle-iOS"].some(text => navigator.userAgent.includes(text));
  const proxy = new Proxy(camelCasedUserData.user.settings, {
    get() {
      if (process.env.APP_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[Deprecation] Found usage of `useUser().settings`, use `useUser().user.settings` instead!");
      }
      return Reflect.get(...arguments);
    }
  });
  const isPayPalOrganization = businessIdentityProvider === BusinessIdentityProvider.PayPal;
  const isTenantUser = identityProvider === IdentityProvider.IzTenant;
  const data = {
    ...camelCasedUserData,
    settings: proxy,
    identityProvider,
    identityProviderId,
    businessIdentityProvider,
    features: featuresData,
    isPayPal: isPayPalOrganization || isTenantUser,
    locale: {
      language: camelCasedUserData.user.languageId,
      country: camelCasedUserData.organization.countryId,
      currency: camelCasedUserData.organization.currencyId,
      isoLocale: `${camelCasedUserData.user.languageId}-${camelCasedUserData.organization.countryId}`
    },
    isMobileApp
  };
  setUser(data);
  onReceiveUser?.(data);
};
const createOverrideData = (user, overrides) => {
  const overriddenData = user != null ? {
    ...user
  } : null;
  if (overriddenData != null && overrides?.betaFeatures != null) {
    overriddenData.organization.betaFeatures = overrides.betaFeatures;
  }
  if (overriddenData != null && overrides?.locale != null) {
    const countryId = CountryIdUtil.fromIsoLocale(overrides.locale);
    const localeData = {
      language: overrides.locale.slice(0, 2),
      country: countryId,
      currency: CurrencyUtil.fromCountry(countryId),
      isoLocale: overrides.locale
    };
    overriddenData.locale = localeData;
    overriddenData.user.languageId = localeData.language;
    overriddenData.organization.countryId = localeData.country;
    overriddenData.organization.currencyId = localeData.currency;
  }
  return overriddenData;
};
const UserProvider = _ref => {
  let {
    onReceiveUser,
    children
  } = _ref;
  const handleError = useErrorHandler();
  const authClient = useAuthClient();
  const sunUrl = useService(KnownService.Sun);
  const overrides = useContext(OverridesContext);
  const [user, setUser] = useState(null);
  useEffect(() => {
    fetchUser(sunUrl, authClient, setUser, onReceiveUser).catch(handleError);
  }, []);
  const overriddenData = createOverrideData(user, overrides);
  return (
    /*#__PURE__*/
    /* @ts-ignore Strict null check bypass */
    jsx(UserContext.Provider, {
      value: overriddenData,
      children: user && children
    })
  );
};

export { UserProvider };
//# sourceMappingURL=user.js.map
